/**
 * Home Care portal — book a nurse / CNA / HHA / therapist visit.
 * Parallel to /nemt. Uses the same corporate branding + patient module.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Heart, Clock, Home, User as UserIcon, Calendar, ArrowRight, CreditCard } from "lucide-react";
import { patientApi } from "@/lib/patientApi";

const API = process.env.REACT_APP_BACKEND_URL + "/api/homecare";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function HomeCare() {
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [visitType, setVisitType] = useState("hourly");
  const [form, setForm] = useState({
    start_at: "", duration_minutes: 240, days_count: 1,
    address: "", task_notes: "",
    member_name: "", member_email: "", member_phone: "",
    payer_type: "private",
  });
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [patient, setPatient] = useState(null); // {id, name, email, phone, default_card_last4, default_card_brand}
  const [useWallet, setUseWallet] = useState(true);

  useEffect(() => {
    fetch(`${API}/caregiver-types`).then((r) => r.json()).then((d) => setTypes(d.caregiver_types || [])).catch(() => {});
    // Silent patient session probe — 401 is expected for guests.
    patientApi.me().then((d) => {
      const u = d?.user || d;
      if (u?.id) {
        setPatient(u);
        setForm((f) => ({
          ...f,
          member_name: f.member_name || u.name || "",
          member_email: f.member_email || u.email || "",
          member_phone: f.member_phone || u.phone || "",
          address: f.address || u.home_address || "",
        }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedType) { setEstimate(null); return; }
    fetch(`${API}/visits/estimate`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ caregiver_type: selectedType, visit_type: visitType, ...form,
        start_at: form.start_at || new Date().toISOString() }),
    }).then((r) => r.json()).then(setEstimate).catch(() => {});
  }, [selectedType, visitType, form.duration_minutes, form.days_count, form.payer_type]);

  const hasCard = !!(patient?.default_payment_method_id || patient?.default_card_last4);
  const willAutoHold = hasCard && useWallet && form.payer_type === "private";

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedType) return toast.error("Choose a caregiver type first");
    setBusy(true);
    try {
      const r = await fetch(`${API}/visits/book`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caregiver_type: selectedType, visit_type: visitType, ...form }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `Error ${r.status}`);
      const visitId = j.visit?.id;
      // If the client has a wallet on file, place the 50% Stripe hold immediately.
      if (willAutoHold && visitId) {
        try {
          const hr = await fetch(`${API}/visits/${visitId}/wallet-hold`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ patient_id: patient.id, visit_id: visitId }),
          });
          const hj = await hr.json();
          if (!hr.ok) throw new Error(hj.detail || `Hold failed (${hr.status})`);
          toast.success(`Visit booked & 50% hold placed on card ending ${patient.default_card_last4 || "••••"}`);
        } catch (err) {
          toast.error(`Booked, but card hold failed: ${err.message}. Dispatch will follow up.`);
        }
      } else {
        toast.success("Visit requested — dispatch will confirm and assign a caregiver.");
      }
      setSelectedType(null); setEstimate(null);
      setForm({ ...form, address: patient?.home_address || "", task_notes: "" });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="homecare-portal">
      <header className="bg-gradient-to-r from-emerald-700 to-emerald-900 text-white px-6 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 mb-4">
            <Heart className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.18em] font-semibold">1st Touch Home Care</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight">Care that comes to you.</h1>
          <p className="text-emerald-100 mt-3 text-lg max-w-xl">Skilled nurses, aides, and therapists on demand. Private-pay or through your insurance.</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {/* Step 1 — pick a caregiver type */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted mb-3">Step 1 · Choose a caregiver</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="caregiver-types">
            {types.map((t) => (
              <button key={t.type} onClick={() => setSelectedType(t.type)}
                data-testid={`caregiver-${t.type}`}
                className={`text-left p-4 rounded-2xl border-2 transition ${selectedType === t.type ? "border-brand-green bg-emerald-50" : "border-brand-navy/10 bg-white hover:border-brand-green/40"}`}>
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted">{t.type}</div>
                <div className="font-serif text-xl text-brand-deep mt-1">{money(t.hourly_rate_cents)}<span className="text-xs text-brand-muted">/hr</span></div>
                <div className="text-xs text-brand-muted mt-1">{t.active_count} available</div>
              </button>
            ))}
          </div>
        </section>

        {selectedType && (
          <form onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-brand-navy/10 p-6 space-y-5" data-testid="visit-form">
            <h2 className="text-xs uppercase tracking-[0.18em] font-semibold text-brand-muted">Step 2 · Visit details</h2>

            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Visit type</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "hourly", label: "Hourly visit", icon: Clock },
                  { id: "live_in", label: "Live-in (24h+)", icon: Home },
                  { id: "per_visit", label: "Per-visit task", icon: Heart },
                ].map((v) => (
                  <button key={v.id} type="button" onClick={() => setVisitType(v.id)}
                    data-testid={`visit-type-${v.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${visitType === v.id ? "bg-brand-green text-white border-brand-green" : "bg-white border-brand-navy/20 text-brand-deep hover:border-brand-green"}`}>
                    <v.icon className="w-4 h-4" /> {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Start date & time *</label>
                <input required type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })}
                  className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-start" />
              </div>
              {visitType === "hourly" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Duration (minutes)</label>
                  <input type="number" min={60} step={30} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                    className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-duration" />
                </div>
              )}
              {visitType === "live_in" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Days</label>
                  <input type="number" min={1} value={form.days_count} onChange={(e) => setForm({ ...form, days_count: parseInt(e.target.value) || 1 })}
                    className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-days" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Home address *</label>
                <input required type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full border border-brand-navy/20 rounded-full px-4 py-3 text-sm" placeholder="Street, city, state" data-testid="visit-address" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Care needs / task notes</label>
                <textarea rows={3} value={form.task_notes} onChange={(e) => setForm({ ...form, task_notes: e.target.value })}
                  className="w-full border border-brand-navy/20 rounded-2xl px-4 py-3 text-sm" placeholder="Medications, mobility needs, allergies, preferences…" data-testid="visit-notes" />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-brand-muted mb-2 block">Payer</label>
              <div className="flex gap-2">
                {[
                  { id: "private", label: "Private pay" },
                  { id: "insurance", label: "Insurance / Medicaid / VA" },
                ].map((p) => (
                  <button key={p.id} type="button" onClick={() => setForm({ ...form, payer_type: p.id })}
                    data-testid={`payer-${p.id}`}
                    className={`px-4 py-2 rounded-full text-sm border ${form.payer_type === p.id ? "bg-brand-green text-white border-brand-green" : "bg-white border-brand-navy/20 text-brand-deep"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input required type="text" value={form.member_name} onChange={(e) => setForm({ ...form, member_name: e.target.value })}
                placeholder="Your name *" className="border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-member-name" />
              <input required type="email" value={form.member_email} onChange={(e) => setForm({ ...form, member_email: e.target.value })}
                placeholder="Email *" className="border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-member-email" />
              <input required type="tel" value={form.member_phone} onChange={(e) => setForm({ ...form, member_phone: e.target.value })}
                placeholder="Phone *" className="border border-brand-navy/20 rounded-full px-4 py-3 text-sm" data-testid="visit-member-phone" />
            </div>

            {estimate && (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between" data-testid="visit-estimate">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-800">Estimated total</div>
                    <div className="font-serif text-3xl text-emerald-900">{money(estimate.total_cents)}</div>
                    {form.payer_type === "private" && estimate.hold_cents > 0 && (
                      <div className="text-xs text-emerald-800 mt-1">50% hold at booking: {money(estimate.hold_cents)}</div>
                    )}
                  </div>
                  <UserIcon className="w-8 h-8 text-emerald-700 opacity-40" />
                </div>
                {hasCard && form.payer_type === "private" && (
                  <label className="flex items-start gap-3 bg-white border border-brand-navy/15 rounded-xl p-3 cursor-pointer hover:border-brand-green"
                    data-testid="use-wallet-toggle">
                    <input type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-emerald-600" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-brand-deep">
                        <CreditCard className="w-4 h-4 text-emerald-700" />
                        Use my card on file — {(patient?.default_card_brand || "card").toUpperCase()} ending {patient?.default_card_last4 || "••••"}
                      </div>
                      <div className="text-xs text-brand-muted mt-0.5">
                        Your 50% hold is placed instantly — no Checkout step. Final total captured when the visit is complete.
                      </div>
                    </div>
                  </label>
                )}
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="submit-visit"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-greenDark text-white font-semibold py-3 rounded-full disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Calendar className="w-4 h-4" /> Request this visit <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
